import React from 'react';

export interface AvatarProps {
  // Data props
  name?: string;
  firstName?: string;
  lastName?: string;
  imageUrl?: string;

  // Styling props
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  bgColor?: string;
  textColor?: string;
  rounded?: boolean;

  // Behavior props
  className?: string;
  onClick?: () => void;
}

const Avatar: React.FC<AvatarProps> = ({
  name,
  firstName,
  lastName,
  imageUrl,
  size = 'md',
  bgColor = 'bg-gray-200',
  textColor = 'text-gray-600',
  rounded = true,
  className = '',
  onClick,
}) => {
  // Generate initials from name
  const getInitials = (): string => {
    // If full name provided
    if (name) {
      const initials = name
        .split(' ')
        .map(n => n?.[0] || '')
        .filter(Boolean)
        .join('')
        .toUpperCase();

      // Return max 2 letters
      return initials.slice(0, 2) || '?';
    }

    // If firstName/lastName provided separately
    if (firstName || lastName) {
      const initials = (
        (firstName?.[0] || '') +
        (lastName?.[0] || '')
      ).toUpperCase();

      return initials || '?';
    }

    return '?';
  };

  // Size mappings
  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-sm',
    lg: 'w-16 h-16 text-xl',
    xl: 'w-24 h-24 text-3xl',
  };

  const sizeClass = sizeClasses[size];
  const roundedClass = rounded ? 'rounded-full' : 'rounded-lg';
  const cursorClass = onClick ? 'cursor-pointer' : '';

  const initials = getInitials();
  const displayName = name || `${firstName || ''} ${lastName || ''}`.trim() || 'User';

  return (
    <div
      className={`
        ${sizeClass}
        ${bgColor}
        ${textColor}
        ${roundedClass}
        ${cursorClass}
        ${className}
        flex items-center justify-center
        font-medium
        overflow-hidden
        flex-shrink-0
      `.trim().replace(/\s+/g, ' ')}
      onClick={onClick}
      title={displayName}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={displayName}
          className={`${sizeClass} object-cover ${roundedClass}`}
        />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  );
};

export default Avatar;
